import { positionRows, studyColumns, projectStudyRows } from './studyMatrix';

const normalize = value => String(value || '').toLowerCase().replace(/ё/g, 'е').replace(/[^a-zа-я0-9]/g, '');

const matches = {
    position: /^(должность|должностьсотрудника|роль|position|role)$/,
    hours: /^(количествоотработанныхчасов|отработаночасов|отработанныечасы|количествочасов|workedhours|hoursworked)$/,
    total: /^(количествочеков|всегочеков|чеков|totalchecks|checkstotal)$/,
    drinks: /^(количествочековснапитками|чековснапитками|checkswithdrinks|drinkchecks|drinkschecks)$/,
    desserts: /^(количествочековсдесертами|чековсдесертами|checkswithdesserts|dessertchecks|dessertschecks)$/,
    drinkShare: /^(долячековснапитками|долячековнапитками|drinkcheckshare)$/,
    dessertShare: /^(долячековсдесертами|долячековдесертами|dessertcheckshare)$/,
};

const readCount = value => {
    if (typeof value === 'number') return Number.isFinite(value) ? value : null;
    const normalized = String(value ?? '').replace(/[\s\u00a0]/g, '').replace(',', '.').replace(/[^\d.-]/g, '');
    if (!normalized) return null;
    const result = Number(normalized);
    return Number.isFinite(result) ? result : null;
};

const share = (part, total) => {
    const numerator = readCount(part);
    const denominator = readCount(total);
    if (numerator === null || denominator === null || denominator <= 0) return '—';
    return `${(numerator / denominator * 100).toLocaleString('ru-RU', { maximumFractionDigits: 1 })}%`;
};

export function withWaiterEfficiencyMetrics(data = {}) {
    const columns = studyColumns(data.titles || []);
    if (!columns.length) return data;
    const findColumn = key => columns.find(column => matches[key].test(normalize(column.leaf)))?.index ?? -1;
    const indices = {
        position: findColumn('position'),
        hours: findColumn('hours'),
        total: findColumn('total'),
        drinks: findColumn('drinks'),
        desserts: findColumn('desserts'),
        drinkShare: findColumn('drinkShare'),
        dessertShare: findColumn('dessertShare'),
    };

    const canCalculateShares = [indices.total, indices.drinks, indices.desserts].every(index => index >= 0);
    const hidden = new Set(canCalculateShares ? [indices.total, indices.drinks, indices.desserts] : []);
    const selected = columns.filter(column => !hidden.has(column.index)).map(column => column.index);
    const newPosition = indices.position < 0;
    const newHours = indices.hours < 0;
    const newDrinkShare = indices.drinkShare < 0;
    const newDessertShare = indices.dessertShare < 0;
    const generatedShareCount = Number(newDrinkShare) + Number(newDessertShare);
    const appendedColumnCount = Number(newPosition) + Number(newHours) + generatedShareCount;

    const titles = projectStudyRows(data.titles || [], selected);
    const hasGroupHeaders = titles.length > 1;
    const addedFields = [];
    if (newPosition) addedFields.push({ id: 'waiter-position', value: 'Должность', sortable: false });
    if (newHours) addedFields.push({ id: 'waiter-hours', value: 'Количество отработанных часов', sortable: false });
    if (newDrinkShare) addedFields.push({ id: 'waiter-drink-share', value: hasGroupHeaders ? 'С напитками' : 'Доля чеков с напитками', sortable: false });
    if (newDessertShare) addedFields.push({ id: 'waiter-dessert-share', value: hasGroupHeaders ? 'С десертами' : 'Доля чеков с десертами', sortable: false });
    if (hasGroupHeaders && generatedShareCount) {
        for (const title of addedFields.filter(field => field.id === 'waiter-position' || field.id === 'waiter-hours')) {
            titles[0].push({ ...title, rowspan: titles.length });
        }
        titles[0].push({ id: 'waiter-check-shares', value: 'Доля чеков', colspan: generatedShareCount, sortable: false });
        titles[titles.length - 1].push(...addedFields.filter(field => field.id === 'waiter-drink-share' || field.id === 'waiter-dessert-share'));
    } else if (titles.length) {
        titles[titles.length - 1].push(...addedFields);
    }

    const positionedRows = positionRows(data.rows || []);
    const rows = projectStudyRows(data.rows || [], selected).map((row, rowIndex) => {
        const original = data.rows[rowIndex] || [];
        const originalSpan = original.reduce((sum, cell) => sum + Math.max(1, Number(cell.colspan) || 1), 0);
        if (original.length === 1 && originalSpan >= columns.length) {
            if (row[0]) row[0].colspan = (Number(row[0].colspan) || selected.length) + appendedColumnCount;
            return row;
        }
        const cells = positionedRows[rowIndex] || [];
        const cellValue = index => cells.find(entry => index >= entry.column && index < entry.column + entry.colspan)?.cell.value;
        const appendedValues = [];
        if (newPosition) appendedValues.push({ id: 'waiter-position', value: '—' });
        if (newHours) appendedValues.push({ id: 'waiter-hours', value: '—' });
        if (newDrinkShare) appendedValues.push({
            id: 'waiter-drink-share',
            value: indices.drinkShare >= 0 ? cellValue(indices.drinkShare) : canCalculateShares ? share(cellValue(indices.drinks), cellValue(indices.total)) : '—',
        });
        if (newDessertShare) appendedValues.push({
            id: 'waiter-dessert-share',
            value: indices.dessertShare >= 0 ? cellValue(indices.dessertShare) : canCalculateShares ? share(cellValue(indices.desserts), cellValue(indices.total)) : '—',
        });
        row.push(...appendedValues);
        return row;
    });

    return { ...data, titles, rows };
}
